import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import cn from "classnames";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import styles from "./ModalDeleteStudent.module.sass";
import Icon from "../Icon";
import Card from "../Card";
import { getStudentDeletionPreview, deleteStudent } from "../../services/student.service";

const ModalDeleteStudent = ({ visible, onClose, studentId, studentName, onDeleted }) => {
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const modalRef = useRef(null);

  // Gère le défilement du corps
  useEffect(() => {
    const targetElement = modalRef.current || document.querySelector("#modal-delete-student");

    if (visible) {
      if (targetElement) {
        disableBodyScroll(targetElement);
      }
      if (studentId) {
        loadPreview();
      }
    } else {
      clearAllBodyScrollLocks();
      setPreview(null);
      setError(null);
      setLoading(true);
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, [visible, studentId]);

  // Gère la touche 'Échap' pour fermer la modale
  const escFunction = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", escFunction);
    return () => {
      document.removeEventListener("keydown", escFunction);
    };
  }, [escFunction]);

  const loadPreview = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getStudentDeletionPreview(studentId);
      setPreview(data);
    } catch (err) {
      setError("Erreur lors du chargement des données");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await deleteStudent(studentId);
      onDeleted?.();
      onClose();
    } catch (err) {
      setError("Erreur lors de la suppression");
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const getTotalItems = () => {
    if (!preview) return 0;
    return (
      preview.documents +
      preview.memos +
      preview.folders +
      preview.contracts +
      preview.attendances +
      preview.sessionGroupMembers +
      (preview.user ? 1 : 0) +
      1
    );
  };

  if (!visible) {
    return null;
  }

  return createPortal(
    <div id="modal-delete-student" className={styles.modal} ref={modalRef}>
      <div className={styles.control}>
        <button className={styles.close} onClick={onClose} aria-label="Fermer la modale">
          <Icon name="close" size="20" />
        </button>
      </div>
      <div className={styles.outer}>
        <Card
          className={styles.card}
          title="Supprimer l'élève"
          classTitle="title-red"
        >
          <div className={styles.container}>
            {loading ? (
              <div className={styles.loading}>
                <div className={styles.spinner}></div>
                <p>Chargement des données...</p>
              </div>
            ) : error ? (
              <div className={styles.error}>
                <Icon name="info-circle" size="20" />
                <p>{error}</p>
              </div>
            ) : (
              <>
                <div className={styles.warning}>
                  <Icon name="info-circle" size="20" />
                  <p>
                    Vous êtes sur le point de supprimer <strong>{studentName}</strong> et toutes ses données associées.
                    Cette action est <strong>irréversible</strong>.
                  </p>
                </div>

                <div className={styles.previewSection}>
                  <h4 className={styles.previewTitle}>Éléments qui seront supprimés :</h4>
                  <ul className={styles.previewList}>
                    <li className={styles.previewItem}>
                      <Icon name="profile-circle" size="18" />
                      <span>1 fiche élève</span>
                    </li>
                    {preview.user && (
                      <li className={styles.previewItem}>
                        <Icon name="lock" size="18" />
                        <span>1 compte utilisateur ({preview.user.email})</span>
                      </li>
                    )}
                    {preview.documents > 0 && (
                      <li className={styles.previewItem}>
                        <Icon name="folder" size="18" />
                        <span>{preview.documents} document{preview.documents > 1 ? 's' : ''}</span>
                      </li>
                    )}
                    {preview.memos > 0 && (
                      <li className={styles.previewItem}>
                        <Icon name="edit" size="18" />
                        <span>{preview.memos} mémo{preview.memos > 1 ? 's' : ''}</span>
                      </li>
                    )}
                    {preview.folders > 0 && (
                      <li className={styles.previewItem}>
                        <Icon name="bag" size="18" />
                        <span>{preview.folders} dossier{preview.folders > 1 ? 's' : ''}</span>
                      </li>
                    )}
                    {preview.contracts > 0 && (
                      <li className={styles.previewItem}>
                        <Icon name="ticket" size="18" />
                        <span>{preview.contracts} contrat{preview.contracts > 1 ? 's' : ''}</span>
                      </li>
                    )}
                    {preview.attendances > 0 && (
                      <li className={styles.previewItem}>
                        <Icon name="check-all" size="18" />
                        <span>{preview.attendances} présence{preview.attendances > 1 ? 's' : ''}</span>
                      </li>
                    )}
                    {preview.sessionGroupMembers > 0 && (
                      <li className={styles.previewItem}>
                        <Icon name="multiselect" size="18" />
                        <span>{preview.sessionGroupMembers} appartenance{preview.sessionGroupMembers > 1 ? 's' : ''} à des groupes</span>
                      </li>
                    )}
                  </ul>
                  <div className={styles.totalCount}>
                    Total : <strong>{getTotalItems()} élément{getTotalItems() > 1 ? 's' : ''}</strong>
                  </div>
                </div>

                <div className={styles.divider} />

                <div className={styles.actions}>
                  <button
                    className={cn("button-stroke", styles.cancelButton)}
                    onClick={onClose}
                    disabled={deleting}
                  >
                    Annuler
                  </button>
                  <button
                    className={cn("button", styles.deleteButton)}
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <div className={styles.buttonSpinner}></div>
                        Suppression...
                      </>
                    ) : (
                      <>
                        <Icon name="trash" size="18" />
                        Supprimer définitivement
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>,
    document.body
  );
};

export default ModalDeleteStudent;
